import { useState } from 'react';
import { Funcionario } from '@/context/FuncionariosContext';
import { calcularProgressao, getStatusColor } from '@/utils/progressaoUtils';
import { useAppSettings } from '@/context/AppSettingsContext';

const PERIODO_BADGE: Record<string, { label: string; color: string; limiarLabel: string }> = {
  pre2023:    { label: '2000–2022', color: 'bg-slate-100 text-slate-500',   limiarLabel: 'Limiar pré-2023' },
  p2023_2024: { label: '2023–2024', color: 'bg-sky-100 text-sky-600',       limiarLabel: 'Limiar 2023–2024' },
  pos2025:    { label: '2025+',     color: 'bg-amber-100 text-amber-600',   limiarLabel: 'Limiar 2025+' },
  misto:      { label: 'Misto',     color: 'bg-violet-100 text-violet-600', limiarLabel: 'Limiar misto' },
  sem_registos: { label: 'Sem avaliações', color: 'bg-slate-100 text-slate-400', limiarLabel: 'Limiar 2025+' },
};

interface FuncionarioRowProps {
  funcionario: Funcionario;
  index: number;
  pontosAtuais: number;
  limiar?: number;
  regraEspecial18Anos?: boolean;
  periodoEscala?: string;
  isPrepav?: boolean;
  onRegistarProgressao?: (f: Funcionario) => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

function NivelArrow({ from, to }: { from: number; to: number }) {
  const diff = to - from;
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500 text-sm">{from}</span>
      <div className="w-5 h-5 flex items-center justify-center text-emerald-500">
        <i className={`${diff >= 2 ? 'ri-arrow-up-double-line' : 'ri-arrow-up-line'} text-base`} />
      </div>
      <span className="font-semibold text-slate-800 text-sm">{to}</span>
      {diff >= 2 && (
        <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">+{diff}</span>
      )}
    </div>
  );
}

export default function FuncionarioRow({ funcionario, index, pontosAtuais, limiar: limiarProp, regraEspecial18Anos = false, periodoEscala, isPrepav = false, onRegistarProgressao }: FuncionarioRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { settings } = useAppSettings();
  const pontosParaProgressao = limiarProp ?? settings.pontos_progressao;

  const prog = calcularProgressao(pontosAtuais, funcionario.nivelAtual, pontosParaProgressao);
  const colors = getStatusColor(prog.status);

  const initials = funcionario.nome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('');

  const avatarColors = [
    'bg-rose-100 text-rose-700',
    'bg-violet-100 text-violet-700',
    'bg-amber-100 text-amber-700',
    'bg-teal-100 text-teal-700',
    'bg-sky-100 text-sky-700',
    'bg-orange-100 text-orange-700',
  ];
  const colorClass = avatarColors[index % avatarColors.length];

  return (
    <>
      <tr
        className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Nº Funcionário */}
        <td className="px-5 py-4">
          <span className="font-mono text-sm text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
            #{funcionario.id}
          </span>
        </td>

        {/* Nome */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold flex-shrink-0 ${colorClass}`}>
              {initials}
            </div>
            <span className="font-medium text-slate-800 text-sm whitespace-nowrap">{funcionario.nome}</span>
          </div>
        </td>

        {/* Carreira + badge escala */}
        <td className="px-5 py-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-slate-600">{funcionario.carreira}</span>
            {periodoEscala && PERIODO_BADGE[periodoEscala] && (
              <span className={`inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded-full w-fit ${PERIODO_BADGE[periodoEscala].color}`}>
                {PERIODO_BADGE[periodoEscala].label}
              </span>
            )}
          </div>
        </td>

        {/* Sexo */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${funcionario.sexo === 'F' ? 'ri-women-line text-rose-400' : 'ri-men-line text-sky-400'} text-base`} />
            </div>
            <span className="text-sm text-slate-600">{funcionario.sexo === 'F' ? 'Feminino' : 'Masculino'}</span>
          </div>
        </td>

        {/* Última Progressão */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center text-slate-400">
              <i className="ri-calendar-line text-sm" />
            </div>
            <span className="text-sm text-slate-600">{formatDate(funcionario.ultimaProgressao)}</span>
          </div>
        </td>

        {/* Novo Nível 2025 */}
        <td className="px-5 py-4">
          <NivelArrow from={funcionario.nivelAtual} to={funcionario.novoNivel2025} />
        </td>

        {/* Pontos */}
        <td className="px-5 py-4">
          <div className="flex flex-col gap-1.5 min-w-[130px]">
            <div className="flex items-center justify-between gap-2">
              <span className={`text-sm font-bold ${colors.text}`}>{pontosAtuais} pts</span>
              {prog.status === 'pronto' ? (
                <div className="flex flex-col items-end gap-0.5">
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${colors.badge}`}>
                    Elegível
                  </span>
                  {regraEspecial18Anos && (
                    <span
                      className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap cursor-help"
                      title="18+ anos de serviço a 30/08/2023 — regra especial 2024 (limiar 6 pts)"
                    >
                      18 anos a 30/08/2023
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-slate-400">/ {pontosParaProgressao}</span>
              )}
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${colors.bar}`}
                style={{ width: `${prog.percentagem}%` }}
              />
            </div>
            {/* Badge de limiar aplicado — auditoria */}
            {periodoEscala && PERIODO_BADGE[periodoEscala] && (
              <span
                className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full w-fit font-medium cursor-help ${PERIODO_BADGE[periodoEscala].color}`}
                title={`${PERIODO_BADGE[periodoEscala].limiarLabel}: ${pontosParaProgressao} pts necessários para progressão`}
              >
                <i className="ri-shield-check-line text-xs" />
                Limiar: {pontosParaProgressao} pts · {PERIODO_BADGE[periodoEscala].label}
              </span>
            )}
            {/* Badge PREPAV — exceção de pontos manuais */}
            {isPrepav && (
              <span
                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full w-fit font-medium bg-orange-100 text-orange-700 cursor-help"
                title="Exceção PREPAV: os pontos manuais são sempre somados aos pontos SIADAP para este funcionário"
              >
                <i className="ri-star-line text-xs" />
                PREPAV
              </span>
            )}
          </div>
        </td>

        {/* Elegibilidade */}
        <td className="px-5 py-4">
          {(() => {
            if (prog.status === 'pronto') {
              return (
                <div className="flex flex-col gap-1">
                  <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full w-fit whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                    Elegível
                  </span>
                  {regraEspecial18Anos && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full w-fit whitespace-nowrap cursor-help" title="18+ anos de serviço a 30/08/2023">
                      <i className="ri-award-line text-[9px]" />
                      18 anos
                    </span>
                  )}
                </div>
              );
            }
            if (prog.status === 'proximo') {
              return (
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-full w-fit whitespace-nowrap">
                  <i className="ri-time-line text-xs" />
                  Próximo
                </span>
              );
            }
            return (
              <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-zinc-400 bg-zinc-50 border border-zinc-100 px-2.5 py-1 rounded-full w-fit whitespace-nowrap">
                <i className="ri-loader-3-line text-xs" />
                Em progresso
              </span>
            );
          })()}
        </td>

        {/* Ações + Expandir */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-1.5 justify-end">
            {onRegistarProgressao && (
              <button
                onClick={(e) => { e.stopPropagation(); onRegistarProgressao(funcionario); }}
                title="Registar progressão"
                className="whitespace-nowrap flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-arrow-up-circle-line text-xs" />
                Progressão
              </button>
            )}
            <div className="w-7 h-7 flex items-center justify-center text-slate-400">
              <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`} />
            </div>
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-slate-50 border-b border-slate-100">
          <td colSpan={9} className="px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center text-slate-400 mt-0.5 flex-shrink-0">
                <i className="ri-file-text-line text-base" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Descrição da Progressão</p>
                <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">{funcionario.descricaoSubida}</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
